import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Accordion, AccordionItem, Tooltip, addToast } from '@heroui/react';
import {
  StudentRequestCategory,
  StudentRequestReason,
  useCreateStudentRequestMutation,
  useGetStudentRequestAttendanceFiltersQuery,
} from '@/entities/StudentRequests';
import { IAttendanceLesson, useGetAttendanceQuery } from '@/entities/Attendance';
import { AppButton } from '@/shared/ui/AppButton';
import { DateSelectField, SelectField, TextareaField } from '@/shared/ui/Fields';
import { COURSE_TYPE } from '@/shared/constants/courseData';
import { getAttendanceColor } from '@/shared/lib/utils/getColorClass';
import { convertIsoToDate, convertIsoToTime } from '@/shared/lib/utils/dateAdapters';
import { ProfileRequestShell } from '../components/ProfileRequestShell';
import { RequestFilesField } from '../components/RequestFilesField';
import {
  STUDENT_ATTENDANCE_REASON_OPTIONS,
  STUDENT_GENERAL_REASON_OPTIONS,
  formatAttendanceValue,
} from '../components/requestUi';

type AttendanceStep = 'filters' | 'form';

const toOptions = (items: { id?: number; title?: string; number?: number; type?: string }[]) =>
  items
    .filter((item): item is { id: number; title?: string; number?: number; type?: string } =>
      typeof item.id === 'number',
    )
    .map((item) => ({
      value: String(item.id),
      label: item.title ?? `Term ${item.number}`,
    }));

const ALL_TYPES = 'all';

const TYPE_OPTIONS = [
  { value: ALL_TYPES, label: 'All types' },
  { value: 'masters-degree', label: COURSE_TYPE['masters-degree'] },
  { value: 'postgraduate', label: COURSE_TYPE.postgraduate },
  { value: 'bachelors-degree', label: COURSE_TYPE['bachelors-degree'] },
  { value: 'short', label: COURSE_TYPE.short },
] as const;

const getLessonTimestamp = (lesson: IAttendanceLesson) => {
  if (!lesson.scheduledAt) return 0;
  const timestamp = new Date(lesson.scheduledAt).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const canSelectLesson = (lesson: IAttendanceLesson) =>
  Boolean(lesson.groupLessonId && lesson.isRequestAllowed !== false);

const getLessonAttendanceValue = (lesson: IAttendanceLesson) =>
  lesson.attendance ?? null;

const formatRequestBlockReason = (reason?: string | null) => {
  if (!reason) return 'Request is not available for this lesson';
  return reason
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message);
  }

  return 'Failed to create request';
};

export const CreateRequestView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialCategory = location.pathname.includes('/attendance') ? 'attendance' : 'general';
  const [category, setCategory] = useState<StudentRequestCategory>(initialCategory);
  const [attendanceStep, setAttendanceStep] = useState<AttendanceStep>('filters');
  const [selectedType, setSelectedType] = useState<string>(ALL_TYPES);
  const [courseId, setCourseId] = useState('');
  const [intakeId, setIntakeId] = useState('');
  const [termId, setTermId] = useState('');
  const [reason, setReason] = useState<StudentRequestReason | ''>('');
  const [comment, setComment] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [selectedLessonIds, setSelectedLessonIds] = useState<number[]>([]);
  const [enrollmentChoice, setEnrollmentChoice] = useState('');

  const { data: attendanceData, isLoading: isFiltersLoading } = useGetAttendanceQuery();
  const [createStudentRequest, { isLoading: isSubmitting }] = useCreateStudentRequestMutation();
  const {
    data: enrollmentCourses,
    isLoading: isEnrollmentsLoading,
    isError: isEnrollmentsError,
  } = useGetStudentRequestAttendanceFiltersQuery();
  const filterCourses = useMemo(() => attendanceData?.courses ?? [], [attendanceData?.courses]);

  const typeOptions = useMemo(() => [...TYPE_OPTIONS], []);

  // До явного выбора показываем все курсы: блок называется Filter и сам ничего не фильтрует.
  const filteredCourses = useMemo(
    () =>
      selectedType === ALL_TYPES
        ? filterCourses
        : filterCourses.filter((course) => (course as { type?: string }).type === selectedType),
    [selectedType, filterCourses],
  );

  const effectiveCourseId = courseId || (filteredCourses[0] ? String(filteredCourses[0].id) : '');
  const selectedCourse = filteredCourses.find((course) => String(course.id) === effectiveCourseId);
  const effectiveIntakeId = intakeId || (selectedCourse?.intake ? String(selectedCourse.intake.id) : '');
  const selectedTerm = selectedCourse?.terms.find((term) => String(term.id) === termId);
  const effectiveTermId =
    termId || (selectedCourse?.terms[0]?.id ? String(selectedCourse.terms[0].id) : '');
  const activeTerm =
    selectedCourse?.terms.find((term) => String(term.id) === effectiveTermId) ?? selectedCourse?.terms[0];
  const enrollmentOptions = useMemo(
    () =>
      (enrollmentCourses ?? []).flatMap((course) =>
        course.intakes.map((intake) => ({
          value: String(intake.enrollmentId),
          // Набор в подписи нужен только курсам, которые идут несколькими наборами:
          // иначе в списке окажутся два одинаковых пункта.
          label: course.intakes.length > 1 ? `${course.title} — ${intake.title}` : course.title,
        })),
      ),
    [enrollmentCourses],
  );
  const hasEnrollments = enrollmentOptions.length > 0;
  const effectiveEnrollmentChoice =
    enrollmentChoice || (enrollmentOptions.length === 1 ? enrollmentOptions[0].value : '');
  // На вкладке Attendance enrollmentId несёт выбранный терм. На вкладке Request выбора не было,
  // и курс брался первый попавшийся из посещаемости — а если её нет, заявку было не отправить.
  const enrollmentId =
    category === 'attendance'
      ? activeTerm?.enrollmentId
      : Number(effectiveEnrollmentChoice) || undefined;

  const courseOptions = useMemo(() => toOptions(filteredCourses), [filteredCourses]);
  const intakeOptions = useMemo(
    () =>
      selectedCourse?.intake
        ? [{ value: String(selectedCourse.intake.id), label: selectedCourse.intake.title }]
        : [],
    [selectedCourse],
  );
  const termOptions = useMemo(() => toOptions(selectedCourse?.terms ?? []), [selectedCourse]);
  const modules = useMemo(() => activeTerm?.modules ?? [], [activeTerm]);
  const sortedModules = useMemo(
    () =>
      modules.map((module) => ({
        ...module,
        lessons: [...module.lessons].sort((a, b) => getLessonTimestamp(a) - getLessonTimestamp(b)),
      })),
    [modules],
  );
  const reasonOptions =
    category === 'attendance' ? STUDENT_ATTENDANCE_REASON_OPTIONS : STUDENT_GENERAL_REASON_OPTIONS;

  const allSelectableLessons = useMemo(
    () =>
      sortedModules.flatMap((module) => module.lessons.filter((lesson) => canSelectLesson(lesson))),
    [sortedModules],
  );

  const handleCategoryChange = (nextCategory: StudentRequestCategory) => {
    setCategory(nextCategory);
    setAttendanceStep('filters');
    // Справочники причин у вкладок разные: чужая причина в поле не покажется,
    // а на сервер уйдёт.
    setReason('');
    navigate(nextCategory === 'attendance' ? '/profile/request/create/attendance' : '/profile/request/create');
  };

  const handleToggleLesson = (lesson: IAttendanceLesson) => {
    if (!lesson.groupLessonId || !canSelectLesson(lesson)) return;

    setSelectedLessonIds((current) =>
      current.includes(lesson.groupLessonId!)
        ? current.filter((id) => id !== lesson.groupLessonId)
        : [...current, lesson.groupLessonId!],
    );
  };

  const handleSubmit = async () => {
    if (!enrollmentId || !reason || !comment.trim()) return;

    const payload = {
      enrollmentId,
      requestCategory: category,
      reason,
      studentComment: comment.trim(),
      studentAttachments: files.length ? files : undefined,
      ...(category === 'attendance'
        ? { groupLessonIds: selectedLessonIds }
        : {
            startDate: startDate ? new Date(startDate).toISOString() : undefined,
            endDate: endDate ? new Date(endDate).toISOString() : undefined,
          }),
    };

    try {
      const result = await createStudentRequest(payload).unwrap();
      navigate(`/profile/requests/${result.id}`);
    } catch (error) {
      addToast({
        title: getErrorMessage(error),
        color: 'danger',
      });
    }
  };

  const canSubmit =
    Boolean(enrollmentId && reason && comment.trim()) &&
    (category === 'general' || selectedLessonIds.length > 0);

  return (
    <ProfileRequestShell title={category === 'attendance' ? 'Attendance request' : 'Create request'}>
      <div className="flex gap-3">
        <button
          type="button"
          className={`rounded-lg px-5 py-3 text-sm font-medium ${category === 'general' ? 'bg-black text-white' : 'text-grey-secondary'}`}
          onClick={() => handleCategoryChange('general')}
        >
          Request
        </button>
        <button
          type="button"
          className={`rounded-lg px-5 py-3 text-sm font-medium ${category === 'attendance' ? 'bg-black text-white' : 'text-grey-secondary'}`}
          onClick={() => handleCategoryChange('attendance')}
        >
          Attendance
        </button>
      </div>

      {category === 'general' ? (
        <div className="flex w-[520px] flex-col gap-5">
          <div>
            <h2 className="text-[36px] font-medium">Request</h2>
            <p className="mt-2 max-w-[360px] text-lg leading-[120%] text-grey-secondary">
              Send a request regarding your studies, documents, or academic needs
            </p>
          </div>
          {hasEnrollments && (
            <SelectField
              name="enrollment"
              label="Course"
              placeholder="Select course"
              value={effectiveEnrollmentChoice}
              options={enrollmentOptions}
              onChange={setEnrollmentChoice}
            />
          )}
          <SelectField
            name="reason"
            label="Reason"
            placeholder="Select reason"
            value={reason}
            options={reasonOptions}
            onChange={(value) => setReason(value as StudentRequestReason)}
          />
          <div className="grid grid-cols-2 gap-5">
            <DateSelectField
              name="startDate"
              label="Period start (optional)"
              value={startDate}
              dateFormat="dd/MM/yyyy"
              onChange={(_, value) => setStartDate(value)}
            />
            <DateSelectField
              name="endDate"
              label="Period end (optional)"
              value={endDate}
              minDate={startDate ? new Date(startDate) : undefined}
              dateFormat="dd/MM/yyyy"
              onChange={(_, value) => setEndDate(value)}
            />
          </div>
          <TextareaField
            name="comment"
            label="Comment"
            placeholder="Enter your comment"
            rows={5}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          <RequestFilesField files={files} onChange={setFiles} />
          <div className="mt-4 grid grid-cols-2 gap-6">
            <AppButton color="white" height={56} onPress={() => navigate('/profile')}>
              Cancel
            </AppButton>
            {hasEnrollments || isEnrollmentsLoading ? (
              <AppButton
                color="red"
                height={56}
                disabled={!canSubmit}
                isLoading={isSubmitting}
                onPress={handleSubmit}
              >
                Submit
              </AppButton>
            ) : (
              <p className="self-center text-grey-secondary">
                {isEnrollmentsError
                  ? 'Could not load your courses. Please try again later.'
                  : 'No courses available for a request yet.'}
              </p>
            )}
          </div>
        </div>
      ) : attendanceStep === 'filters' ? (
        <div className="flex flex-col gap-8">
          <Accordion
            defaultExpandedKeys={['filter']}
            variant="splitted"
            className="p-0"
            itemClasses={{
              base: 'rounded-lg border border-[#D9D9D9] p-6 shadow-none',
              trigger: 'px-0 py-0 border-0 cursor-pointer',
              title: 'text-lg font-medium',
              content: 'px-0 pt-5 border-0',
              heading: 'border-0',
              indicator: 'w-6 h-6 text-xl text-grey-secondary',
            }}
          >
            <AccordionItem key="filter" textValue="Filter" title="Filter">
              <div className="grid max-w-[980px] grid-cols-2 gap-5">
                <SelectField
                  name="type"
                  label="Type"
                  value={selectedType}
                  options={typeOptions}
                  readOnly={typeOptions.length <= 1}
                  onChange={(value) => {
                    if (typeOptions.length <= 1) return;
                    setSelectedType(value);
                    const nextCourses =
                      value === ALL_TYPES
                        ? filterCourses
                        : filterCourses.filter(
                            (course) => (course as { type?: string }).type === value,
                          );
                    const nextCourse = nextCourses[0];
                    const nextTerm = nextCourse?.terms[0];
                    setCourseId(nextCourse ? String(nextCourse.id) : '');
                    setIntakeId(nextCourse?.intake ? String(nextCourse.intake.id) : '');
                    setTermId(nextTerm ? String(nextTerm.id) : '');
                    setSelectedLessonIds([]);
                  }}
                />
                <SelectField
                  name="intake"
                  label="Intake"
                  value={effectiveIntakeId}
                  options={intakeOptions}
                  onChange={(value) => {
                    setIntakeId(value);
                    setSelectedLessonIds([]);
                  }}
                />
                <SelectField
                  name="course"
                  label="Course"
                  value={effectiveCourseId}
                  options={courseOptions}
                  onChange={(value) => {
                    const nextCourse = filterCourses.find((course) => String(course.id) === value);
                    const nextTerm = nextCourse?.terms[0];
                    setCourseId(value);
                    setIntakeId(nextCourse?.intake ? String(nextCourse.intake.id) : '');
                    setTermId(nextTerm ? String(nextTerm.id) : '');
                    setSelectedLessonIds([]);
                  }}
                />
                <SelectField
                  name="term"
                  label="Term"
                  placeholder="Select term"
                  value={effectiveTermId}
                  options={termOptions}
                  onChange={(value) => {
                    setTermId(value);
                    setSelectedLessonIds([]);
                  }}
                />
              </div>
            </AccordionItem>
          </Accordion>

          <div className="flex flex-col gap-6">
            {sortedModules.map((module) => (
              <div key={module.id} className="rounded-lg bg-[#FAFAFA] p-4">
                <h3 className="text-xl font-semibold">
                  {module.title} {selectedTerm ? `(Term ${selectedTerm.number})` : ''}
                </h3>
                <div className="mt-4 overflow-x-auto">
                  <div className="flex min-w-max">
                    {module.lessons.map((lesson) => {
                      const isSelectable = canSelectLesson(lesson);
                      const attendanceValue = getLessonAttendanceValue(lesson);
                      const colors = getAttendanceColor(attendanceValue);
                      const blockReason = formatRequestBlockReason(lesson.requestBlockReason);
                      const tooltipText = lesson.title || blockReason;
                      const selected = Boolean(
                        lesson.groupLessonId && selectedLessonIds.includes(lesson.groupLessonId),
                      );

                      const lessonButton = (
                        <button
                          key={lesson.id}
                          type="button"
                          title={tooltipText}
                          aria-disabled={!isSelectable}
                          className={`min-w-[128px] border border-[#E5E7EB] bg-white ${
                            isSelectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                          }`}
                          onClick={() => {
                            if (!isSelectable) return;
                            handleToggleLesson(lesson);
                          }}
                        >
                          <div className="h-10 border-b border-[#E5E7EB] bg-[#F4F4F5] px-2 py-3 text-sm font-semibold text-grey-description">
                            {convertIsoToDate(lesson.scheduledAt)}
                          </div>
                          <div className="grid grid-cols-2 border-b border-[#E5E7EB] text-sm text-grey-secondary">
                            <span className="py-3">{convertIsoToTime(lesson.scheduledAt)}</span>
                            <span className="py-3">{selected && '✓'}</span>
                          </div>
                          <div
                            className="py-3 text-sm font-semibold"
                            style={{ backgroundColor: colors.bg, color: colors.text }}
                          >
                            {formatAttendanceValue(attendanceValue)}
                          </div>
                        </button>
                      );

                      return (
                        <Tooltip
                          key={lesson.id}
                          content={tooltipText}
                          placement="top"
                          showArrow
                          classNames={{
                            content: 'bg-[#211D55] px-3 py-2 text-sm font-medium text-white',
                          }}
                        >
                          <span className="inline-block">{lessonButton}</span>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
            {!isFiltersLoading && !allSelectableLessons.length && (
              <p className="text-grey-secondary">No lessons available for this term.</p>
            )}
          </div>
          <div className="flex justify-start w-full">
            <AppButton
              color="red"
              className="rounded-[200px] px-4 py-[10px] !min-w-[350px] bg-[#630000] border-[#630000] text-white text-base font-medium leading-[150%] tracking-[0px] hover:bg-[#4E0000] hover:border-[#4E0000]"
              disabled={!selectedLessonIds.length}
              onPress={() => setAttendanceStep('form')}
            >
              Next
            </AppButton>
          </div>
        </div>
      ) : (
        <div className="flex w-[520px] flex-col gap-6">
          <div>
            <p className="text-xl font-semibold">{selectedCourse?.title}</p>
            <p className="mt-1 text-xl font-semibold">Term {activeTerm?.number ?? selectedTerm?.number ?? '-'}</p>
          </div>
          <div className="flex flex-col gap-5">
            <SelectField
              name="attendanceReason"
              label="Reason"
              placeholder="Select reason"
              value={reason}
              options={reasonOptions}
              onChange={(value) => setReason(value as StudentRequestReason)}
            />
            <TextareaField
              name="attendanceComment"
              label="Comment"
              placeholder="Enter your comment"
              rows={5}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
            <RequestFilesField files={files} onChange={setFiles} />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-6">
            <AppButton color="white" height={56} onPress={() => setAttendanceStep('filters')}>
              Cancel
            </AppButton>
            <AppButton color="red" height={56} disabled={!canSubmit} isLoading={isSubmitting} onPress={handleSubmit}>
              Submit
            </AppButton>
          </div>
        </div>
      )}
    </ProfileRequestShell>
  );
};
